import React, { useState, useEffect, useRef } from "react";
import { Upload, message, Button, Progress } from "antd";
import "./UploadClassVideos.css";
import VideoItem from "../VideoItem/VideoItem";
import { createVideoId } from "../../Utilities/videoApi";
import { CloudUploadOutlined, PauseOutlined, PlayCircleOutlined } from "@ant-design/icons";

const UploadClassVideos = ({ classId, classVideos }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUploadingAll, setIsUploadingAll] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(-1);
  const uploadQueueRef = useRef([]);
  const currentUploadIndexRef = useRef(-1);

  const accessToken = localStorage.getItem("accessToken");
  const storedDeviceId = localStorage.getItem("deviceId");
  const storedUserIp = localStorage.getItem("userIp");

  // Keep refs in sync with state
  useEffect(() => {
  uploadQueueRef.current = uploadQueue;
  currentUploadIndexRef.current = currentUploadIndex;
  }, [uploadQueue, currentUploadIndex]);

  const formatTitle = (str) => {
  if (!str) return str;
  
  return str
    .toLowerCase()
    .split(' ')
    .map(word => {
      if (word.length === 0) return word;
      // Handle hyphenated words (e.g., "state-of-the-art")
      if (word.includes('-')) {
        return word.split('-')
          .map(subWord => subWord.charAt(0).toUpperCase() + subWord.slice(1))
          .join('-');
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

    useEffect(() => {
    console.log("Initializing videos from classVideos", classVideos);
    
    if (classVideos && classVideos.videos) {
      // Sort videos by updated_at in descending order (newest first)
      const sortedVideos = [...classVideos.videos].sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });

      const initialVideos = sortedVideos.map((video, index) => ({
        videoId: video.video_id || video.id || `temp-${Date.now()}-${index}`,
        title: video.title || video.file?.name || `Video ${index + 1}`,
        file: null,
        isUploaded: true,
        isIntro: video.is_intro,
        thumbnail: video.thumbnail_url || null,
        thumbnailUploaded: !!video.thumbnail_url,
        uploadProgress: 100, 
        existingVideo: true,
        createdAt: video.created_at || new Date().toISOString(),
        // Add status for queue management
        uploadStatus: 'completed'
      }));
      
      console.log("Initialized videos:", initialVideos);
      setVideos(initialVideos);
    } else {
      console.log("No existing class videos found");
      setVideos([]);
    }
  }, [classVideos]);

  const handleInputChange = (videoId, field, value) => {
    console.log(`Updating video ${videoId}: ${field} = ${value}`);
    setVideos(videos.map(video => 
      video.videoId === videoId ? { ...video, [field]: value } : video
    ));
  };

    const handleThumbnailUpload = (videoId, file) => {
    console.log(`Uploading thumbnail for video ${videoId}`);
    setVideos(videos.map(video => {
      if (video.videoId === videoId) {
        // Clean up previous thumbnail if it was a File object
        if (video.thumbnail instanceof File) {
          URL.revokeObjectURL(video.thumbnail);
        }
        return { 
          ...video, 
          thumbnail: file,
          thumbnailUploaded: false
        };
      }
      return video;
    }));
  };

  const handleRemoveVideo = async (videoId) => {
    setVideos(videos.filter(video => video.videoId !== videoId));
  };

  const handleCreateVideo = async (file) => {
    console.log("Creating new video entry");
    setLoading(true);
    try {
      const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
      const title = formatTitle(fileNameWithoutExtension);
      
      console.log("Creating video ID...");
      const videoId = await createVideoId(
        file,
        classId,
        title,
        videos.length === 0,
        accessToken,
        storedDeviceId,
        storedUserIp
      );
      
      const newVideo = {
        videoId,
        title,
        file,
        isUploaded: false,
        uploadProgress: 0,
        isIntro: videos.length === 0,
        thumbnail: null,
        thumbnailUploaded: false,
        existingVideo: false,
        uploadStatus: 'pending' // Add upload status
      };
      
      setVideos(prevVideos => [...prevVideos, newVideo]);
      message.success('Video added successfully. Please fill in details and upload.');
    } catch (error) {
      console.error('Error handling upload:', error);
      message.error('Failed to add video. ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Add this function to start sequential upload
  const handleUploadAllVideos = () => {
    // Filter videos that need uploading
    const videosToUpload = videos.filter(video => 
      !video.isUploaded && 
      video.file && 
      !video.existingVideo
    );

    if (videosToUpload.length === 0) {
      message.info("No videos to upload. All videos are already uploaded.");
      return;
    }

    // Create upload queue
    const queue = videosToUpload.map(video => ({
      videoId: video.videoId,
      title: video.title,
      file: video.file
    }));

    setUploadQueue(queue);
    setCurrentUploadIndex(0);
    setIsUploadingAll(true);
    
    // Start the first upload
    startNextUpload();
  };

  // Add this function to stop sequential upload
  const handleStopUploadAll = () => {
    setCurrentUploadIndex(-1);
    setIsUploadingAll(false);
    setUploadQueue([]);
    message.info("Upload sequence stopped.");
  };

  // Function to start next upload in sequence
  const startNextUpload = () => {
    if (currentUploadIndexRef.current >= uploadQueueRef.current.length) {
      // All videos uploaded
      setIsUploadingAll(false);
      setCurrentUploadIndex(-1);
      setUploadQueue([]);
      message.success("All videos uploaded successfully!");
      return;
    }

    const currentVideo = uploadQueueRef.current[currentUploadIndexRef.current];
    
    if (currentVideo) {
      // Trigger upload for this video
      // We'll need to pass a callback to VideoItem to notify when upload is complete
    }
  };

  // Callback when a video upload completes
  const handleVideoUploadComplete = (videoId, success) => {
    if (!isUploadingAll) return;

    if (success) {
      // Update video status
      setVideos(prev => prev.map(video => 
        video.videoId === videoId 
          ? { ...video, isUploaded: true, uploadProgress: 100, uploadStatus: 'completed' }
          : video
      ));

      // Move to next video
      const nextIndex = currentUploadIndexRef.current + 1;
      setCurrentUploadIndex(nextIndex);
      
      // Start next upload after a short delay
      setTimeout(() => {
        startNextUpload();
      }, 1000);
    } else {
      // Stop the sequence on failure
      message.error(`Upload failed for video. Sequence stopped.`);
      handleStopUploadAll();
    }
  };

  return (
    <div className="upload-class-videos-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h4 style={{ margin: 0 }}>Upload Class Videos</h4>
        
        {videos.filter(v => !v.isUploaded && v.file).length > 0 && (
          <div>
            {!isUploadingAll ? (
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                onClick={handleUploadAllVideos}
                loading={loading}
                disabled={loading}
              >
                Upload All Videos ({videos.filter(v => !v.isUploaded && v.file).length})
              </Button>
            ) : (
              <Button
                type="default"
                danger
                icon={<PauseOutlined />}
                onClick={handleStopUploadAll}
              >
                Stop Upload Sequence
              </Button>
            )}
          </div>
        )}
      </div>

      <p className="upload-class-videos-sum-text">
        If you're teaching your first class on BeyondSchools, we recommend reviewing
        in our Teacher Help Center for a full overview of
        the class creation process.
      </p>

      {isUploadingAll && uploadQueue.length > 0 && (
        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f5ff', borderRadius: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>
              Uploading video {currentUploadIndex + 1} of {uploadQueue.length}: 
              <strong> {uploadQueue[currentUploadIndex]?.title}</strong>
            </span>
            <Progress
              percent={Math.round(((currentUploadIndex) / uploadQueue.length) * 100)}
              style={{ width: '200px', margin: 0 }}
              size="small"
            />
          </div>
        </div>
      )}

      <div className="upload-class-videos-render">
        <Upload.Dragger
          multiple={true}
          accept="video/*"
          showUploadList={false} 
          className="upload-dragger"
          disabled={loading || isUploadingAll}
          beforeUpload={(file) => {
            handleCreateVideo(file);
            return false;
          }}
        >
          <p className="upload-text">
            {loading ? "Processing..." : 
            (videos.length === 0 ? "Drop your video here, or browse" : `You can add more videos`)}
          </p>
        </Upload.Dragger>

        {videos.length > 0 && (
          <div className="video-list">
            {videos.map((video, index) => (
              <VideoItem
                key={video.videoId}
                video={video}
                handleInputChange={handleInputChange}
                handleRemoveVideo={handleRemoveVideo}
                handleThumbnailUpload={handleThumbnailUpload}
                onUploadComplete={handleVideoUploadComplete}
                isUploadingAll={isUploadingAll}
                isCurrentUpload={isUploadingAll && currentUploadIndex === index}
                uploadDisabled={isUploadingAll && !video.isUploaded && currentUploadIndex !== index}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadClassVideos;